/**
 * The date, kept current.
 *
 * `today()` in lib/dates.js is read inside computeds and templates all over the
 * app — the month on screen, a budget's period, an automatic plan's figure,
 * "due in 3 days". A plain call to the system clock is invisible to Vue, so a
 * tab left open past midnight went on showing yesterday until something else
 * happened to change. Handing dates.js a clock that reads `state.today` makes
 * every one of those reactive at once; this file's only other job is to move
 * `state.today` when the wall clock does.
 *
 * Three ways, because no one of them is enough: a timer for local midnight
 * (exact, but a sleeping laptop or a throttled background tab lets it slip),
 * a check whenever the page comes back into view or focus (the moment it
 * matters most), and a slow poll underneath both. Each only writes when the
 * date has actually changed, so nothing is redrawn for nothing.
 */
import { realToday, setDateClock } from '../lib/dates'
import { state } from './state'

setDateClock(() => state.today)

/** Moves `state.today` to the real date if it has changed. Returns the date. */
export function tick() {
  const now = realToday()
  if (state.today !== now) state.today = now
  return now
}

/** Milliseconds from `now` until just after the next local midnight. */
export function untilMidnight(now = new Date()) {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1)
  return Math.max(1000, next - now)
}

/**
 * Starts keeping the date current. Browser-only; returns a function that
 * stops it again.
 */
export function startClock({ poll = 30_000 } = {}) {
  if (typeof window === 'undefined') return () => {}
  let midnight
  const arm = () => {
    clearTimeout(midnight)
    midnight = setTimeout(() => {
      tick()
      arm()
    }, untilMidnight())
  }
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return
    tick()
    // Asleep across midnight, the timer may have fired late or not at all.
    arm()
  }
  const interval = setInterval(tick, poll)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('focus', tick)
  tick()
  arm()
  return () => {
    clearTimeout(midnight)
    clearInterval(interval)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('focus', tick)
  }
}
